import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Pagination,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Chip
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import AdminNavbar from './AdminNavbar';
import './AdminModern.css';

const GuestRegistrations = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 20
  });
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();

  const fetchSubmissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.limit
      });

      if (search) params.append('search', search);

      const apiBase = process.env.REACT_APP_API_URL || '';
      const guestUrl = apiBase.endsWith('/') ? apiBase + 'admin/guest-registrations' : apiBase + '/admin/guest-registrations';
      const response = await axios.get(`${guestUrl}?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setSubmissions(response.data.submissions);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error('Error fetching guest registrations:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
      } else {
        setError('Failed to load guest registrations');
      }
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.limit, search, navigate]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, currentPage: value }));
  };

  const clearFilters = () => {
    setSearch('');
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const handleBack = () => {
    navigate('/admin/dashboard');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record? This action cannot be undone.')) {
      return;
    }

    setDeletingId(id);
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const apiBase = process.env.REACT_APP_API_URL || '';
      const deleteUrl = apiBase.endsWith('/') ? apiBase + `admin/guest-registrations/${id}` : apiBase + `/admin/guest-registrations/${id}`;
      await axios.delete(deleteUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Refresh the data after successful deletion
      fetchSubmissions();
    } catch (err) {
      console.error('Error deleting record:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
      } else {
        setError('Failed to delete record');
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }


      const apiBase = process.env.REACT_APP_API_URL || '';
      const downloadUrl = apiBase.endsWith('/')
        ? apiBase + 'admin/download-guest-registrations-csv'
        : apiBase + '/admin/download-guest-registrations-csv';
      const response = await axios.get(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'guest_registrations.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading CSV:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
      } else {
        setError('Failed to download CSV');
      }
    }
  };

  if (loading) {
    return (
      <div className="admin-page-wrapper">
        <div className="admin-bg" />
        <AdminNavbar />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page-wrapper">
        <div className="admin-bg" />
        <AdminNavbar />
        <Box sx={{ p: 3 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </div>
    );
  }

  return (
    <div className="admin-page-wrapper">
      <div className="admin-bg" />
      <AdminNavbar />
      <div className="admin-main-container">
        {/* Header */}
        <div className="admin-glass-card">
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton onClick={handleBack} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1">
              Guest Registrations
            </Typography>
          </Box>
        </div>

        {/* Search Section */}
        <div className="admin-glass-card">
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }} className="search-filter-container">
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label="Search"
                value={search}
                onChange={handleSearchChange}
                placeholder="Search by participant name or supplier name"
                sx={{ minWidth: { xs: '100%', sm: 300 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <Button
                className="admin-btn"
                onClick={clearFilters}
                disabled={!search}
                sx={{
                  backgroundColor: '#000000',
                  color: '#ffffff',
                  fontWeight: '500',
                  '&:hover': {
                    backgroundColor: '#333333',
                    color: '#ffffff'
                  },
                  '&:disabled': {
                    backgroundColor: '#cccccc',
                    color: '#666666'
                  }
                }}
              >
                Clear Filters
              </Button>
            </Box>

            <Button
              className="admin-btn"
              onClick={handleDownloadCSV}
              startIcon={<DownloadIcon />}
              sx={{
                backgroundColor: '#4CAF50',
                color: '#ffffff',
                fontWeight: '500',
                '&:hover': {
                  backgroundColor: '#45a049',
                  color: '#ffffff'
                }
              }}
            >
              Download CSV
            </Button>
          </Box>
        </div>

        {/* Results Summary */}
        <div className="admin-glass-card">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {submissions.length} of {pagination.totalCount} guest registrations
            {search && ` for "${search}"`}
          </Typography>
        </div>

        {/* Data Table */}
        <div className="admin-glass-card">
          <div className="admin-table-responsive">
            <TableContainer>
              <Table className="admin-table">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Participant Name</TableCell>
                    <TableCell>Supplier Name</TableCell>
                    <TableCell>Guest Count</TableCell>
                    <TableCell>Remark</TableCell>
                    <TableCell>Submitted At</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submissions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No guest registrations found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    submissions.map((submission, index) => (
                      <TableRow key={submission.id || index}>
                        <TableCell>{submission.id}</TableCell>
                        <TableCell>{submission.participant_name}</TableCell>
                        <TableCell>{submission.supplier_name}</TableCell>
                        <TableCell>
                          <Chip 
                            label={submission.guest_count || 1} 
                            size="small" 
                            color="primary"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell>
                          {submission.remark ? (
                            <Typography variant="body2" sx={{ 
                              maxWidth: { xs: 200, sm: 400, md: 600 }, 
                              wordBreak: 'break-word',
                              whiteSpace: 'pre-wrap',
                              lineHeight: 1.4
                            }}>
                              {submission.remark}
                            </Typography>
                          ) : (
                            <Chip label="No remark" size="small" color="default" />
                          )}
                        </TableCell>
                        <TableCell>{formatDate(submission.submitted_at)}</TableCell>
                        <TableCell>
                          <IconButton
                            onClick={() => handleDelete(submission.id)}
                            disabled={deletingId === submission.id}
                            color="error"
                            size="small"
                            title="Delete record"
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(244, 67, 54, 0.1)'
                              }
                            }}
                          >
                            {deletingId === submission.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <DeleteIcon />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="admin-glass-card">
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestRegistrations; 